import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { api } from "@/lib/backend";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { Capacitor } from "@/lib/capacitor";
import { Device } from "@/lib/capacitor/device";
import { PushNotifications } from "@/lib/capacitor/push-notifications";
import { useMe } from "@/lib/hooks/use-me";
import { queryOptions_pushNotificationsPermissions } from "@/lib/providers/query/functions/@capacitor/push-notifications/permissions";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { logger } from "@/logger";

// Define notification data schema
// The backend should send notifications with this data structure:
// {
//   "notification": {
//     "title": "Shift Reminder",
//     "body": "Your shift starts in 30 minutes"
//   },
//   "data": {
//     "path": "https://app.example.com/path/to/content", // URL to navigate to
//     "type": "shift_reminder",     // Optional: Type for analytics
//     "metadata": { ... }          // Optional: Any additional data
//   }
// }
const NotificationDataSchema = z
  .object({
    path: z.string().optional(), // URL to navigate to when notification is tapped
    type: z.string().optional(), // Optional type for analytics/logging
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

export function NotificationListenerPush() {
  const hasRegisteredRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  const { me } = useMe();
  const userId = me?.id ?? null;

  const { data: permissionState } = useQuery(queryOptions_pushNotificationsPermissions());

  const mutationAddDevice = useExtractedMutation({
    mutationKey: ["notification", "add-device"],
    mutationFn: api.notification.addDevice,
  });

  // Set up notification listeners
  useEffect(() => {
    if (Capacitor.Platform === "web") {
      return;
    }

    // Registration success listener
    PushNotifications.addRegistrationListener(async (token) => {
      logger.debug({ token }, "Push Registration Success");
      try {
        const deviceIdentifier = await Device.getDeviceIdentifier();
        const deviceName = await Device.getDeviceName();
        const platform = Capacitor.Platform;

        await mutationAddDevice.mutateAsync({
          token,
          name: deviceName,
          deviceIdentifier,
          platform,
        });

        logger.info("Device registered successfully with backend", {
          deviceIdentifier,
          platform,
        });
      } catch (error) {
        logger.error({ error }, "Failed to register device with backend");
      }
    });

    // Registration error listener
    PushNotifications.addRegistrationErrorListener((error) =>
      logger.error({ error }, "Push Notification Registration Error"),
    );

    // Notification received listener (app in foreground)
    PushNotifications.addPushNotificationReceivedListener((notification) => {
      logger.info({ notification }, "Push Notification Received");

      // Show toast notification when app is in foreground
      const { title, body } = notification;
      if (title || body) {
        toast(title || "New Notification", {
          description: body,
          duration: 5000,
          action: {
            label: "View",
            onClick: () => {
              // Simulate notification tap
              PushNotifications.addPushNotificationActionPerformedListener((action) => {
                // This will trigger the navigation logic
              });

              // Parse and navigate
              try {
                const rawData = notification.data || {};
                const data = NotificationDataSchema.safeParse(rawData);

                if (data.success && data.data.path) {
                  const path = data.data.path.startsWith("/")
                    ? data.data.path
                    : `/${data.data.path}`;
                  logger.info(`Navigating to path: ${path}`);
                  history.push(path);
                }
              } catch (error) {
                logger.error({ error }, "Error navigating from toast");
              }
            },
          },
        });
      }
    });

    // Notification action performed listener (user tapped notification)
    PushNotifications.addPushNotificationActionPerformedListener((action) => {
      logger.info({ action }, "Push Notification Action Performed");

      // Parse and handle notification data
      try {
        const { notification } = action;
        const rawData = notification.data || {};
        const data = NotificationDataSchema.safeParse(rawData);

        if (!data.success) {
          logger.warn({ error: data.error, rawData }, "Failed to parse notification data");
          return;
        }

        const { path, type } = data.data;

        if (path) {
          logger.info(`Navigating to path: ${path}`, { type });
          history.push(path);
        } else {
          // No deeplink provided, navigate to app home
          logger.info("No path provided, going to app home");
          history.push(Routes.App());
        }
      } catch (error) {
        logger.error({ error }, "Error handling notification navigation");
      }
    });

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [mutationAddDevice]);

  // Handle registration when permissions are granted or user changes
  useEffect(() => {
    if (Capacitor.Platform === "web" || !userId) {
      return;
    }

    // Check if user has changed (account switch)
    const userChanged = currentUserIdRef.current !== null && currentUserIdRef.current !== userId;

    if (userChanged) {
      logger.info("User changed, resetting registration state", {
        oldUserId: currentUserIdRef.current,
        newUserId: userId,
      });
      hasRegisteredRef.current = false;
    }

    currentUserIdRef.current = userId;

    // Only register if permission granted and not already registered for this user
    if (permissionState !== "granted" || hasRegisteredRef.current) {
      return;
    }

    const registerForPushNotifications = async () => {
      try {
        logger.info("Registering for push notifications...", { userId });
        await PushNotifications.register();
        hasRegisteredRef.current = true;
      } catch (error) {
        logger.error({ error }, "Failed to register for push notifications");
      }
    };

    registerForPushNotifications();
  }, [permissionState, userId]);

  return null;
}
